import { useState } from "react";
import Toggle from "@/components/base/Toggle";
import { garments, washTypes, delivery } from "@/mocks/services";
import type { Garment } from "@/mocks/services";

export default function LaundryTab() {
  const [items, setItems] = useState<Garment[]>(garments);
  const [expanded, setExpanded] = useState<number | null>(1);
  const [saved, setSaved] = useState(false);
  const [marzam, setMarzam] = useState("مرزام");
  const [starch, setStarch] = useState("بدون");
  const [urgentMode, setUrgentMode] = useState<"percent" | "fixed">("percent");
  const [urgentValue, setUrgentValue] = useState("50");

  const flash = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  const toggleGarment = (id: number) => {
    setItems((prev) => prev.map((g) => (g.id === id ? { ...g, enabled: !g.enabled } : g)));
    flash();
  };

  return (
    <div className="space-y-5">
      {/* Available garments */}
      <div className="rounded-[20px] bg-white p-6 shadow-card">
        <h2 className="text-lg font-semibold text-ghasla-text-heading">القطع المتاحة</h2>
        <p className="mt-1 text-[13px] text-ghasla-text-secondary">فعّل القطع اللي تشتغل عليها من القائمة الرئيسية</p>
        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {items.map((g) => (
            <div
              key={g.id}
              className={`flex items-center gap-3 rounded-xl border p-3 transition-colors ${
                g.enabled ? "border-[#E4EFEF] bg-[#F0FDFD]" : "border-[#F4F8F8] bg-white opacity-60"
              }`}
            >
              <Toggle size="sm" checked={g.enabled} onChange={() => toggleGarment(g.id)} />
              <span className="flex items-center gap-2 text-sm font-medium text-ghasla-text-heading">
                <i className={`${g.icon} text-ghasla-text-secondary`} />
                {g.name}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Prices per garment */}
      <div className="rounded-[20px] bg-white p-6 shadow-card">
        <h2 className="text-lg font-semibold text-ghasla-text-heading">أسعار الخدمات لكل قطعة</h2>
        <div className="mt-4 space-y-3">
          {items.filter((g) => g.enabled).map((g) => (
            <div key={g.id} className="overflow-hidden rounded-xl border border-[#F4F8F8]">
              <button
                type="button"
                onClick={() => setExpanded(expanded === g.id ? null : g.id)}
                className="flex w-full cursor-pointer items-center justify-between bg-[#F0FDFD] px-4 py-3 text-right"
              >
                <span className="flex items-center gap-2 text-sm font-semibold text-ghasla-text-heading">
                  <i className={`${g.icon} text-ghasla-text-secondary`} />
                  {g.name}
                </span>
                <i
                  className={`ri-arrow-down-s-line text-lg text-ghasla-text-secondary transition-transform ${
                    expanded === g.id ? "rotate-180" : ""
                  }`}
                />
              </button>
              {expanded === g.id && (
                <div className="px-4 py-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs font-semibold text-ghasla-text-secondary">
                        <th className="h-9 text-right">الخدمة</th>
                        <th className="h-9 text-center">السعر</th>
                        <th className="h-9 text-center">مفعّل</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.services.map((s, i) => (
                        <tr key={i} className="border-t border-[#F4F8F8]">
                          <td className="h-11 text-ghasla-text-primary">{s.name}</td>
                          <td className="h-11">
                            <div className="flex items-center justify-center gap-1">
                              <input
                                className="h-8 w-16 rounded-lg border border-[#E4EFEF] bg-white text-center text-sm outline-none focus:border-[#0D6E6E]"
                                defaultValue={s.price}
                                onChange={flash}
                              />
                              <span className="text-xs text-ghasla-text-secondary">ر.س</span>
                            </div>
                          </td>
                          <td className="h-11">
                            <div className="flex justify-center">
                              <Toggle size="sm" checked={s.enabled} />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {g.hasOptions && (
                    <div className="mt-4 rounded-xl border border-[#F4F8F8] bg-[#FAFEFE] p-4">
                      <p className="text-xs font-semibold text-[#B45309]">الخيارات الإضافية:</p>

                      <div className="mt-3">
                        <p className="text-xs font-medium text-ghasla-text-primary">المرزام:</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {[
                            { label: "مرزام (+3 ر.س)", value: "مرزام" },
                            { label: "بدون (0)", value: "بدون" },
                          ].map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => {
                                setMarzam(opt.value);
                                flash();
                              }}
                              className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                                marzam === opt.value
                                  ? "bg-[#0D6E6E] text-white"
                                  : "bg-[#F0FDFD] text-ghasla-text-primary hover:bg-[#E8F0F0]"
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="mt-4">
                        <p className="text-xs font-medium text-ghasla-text-primary">النشا:</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {[
                            { label: "بدون (0)", value: "بدون" },
                            { label: "خفيف (+2 ر.س)", value: "خفيف" },
                            { label: "وسط (+3 ر.س)", value: "وسط" },
                            { label: "ثقيل (+4 ر.س)", value: "ثقيل" },
                          ].map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => {
                                setStarch(opt.value);
                                flash();
                              }}
                              className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                                starch === opt.value
                                  ? "bg-[#0D6E6E] text-white"
                                  : "bg-[#F0FDFD] text-ghasla-text-primary hover:bg-[#E8F0F0]"
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Wash types */}
      <div className="rounded-[20px] bg-white p-6 shadow-card">
        <h2 className="text-lg font-semibold text-ghasla-text-heading">أسعار أنواع الغسيل</h2>
        <p className="mt-1 text-[13px] text-ghasla-text-secondary">السعر الأساسي هو للغسيل العادي. حدد الزيادة لباقي الأنواع</p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[420px] text-sm">
            <thead>
              <tr className="text-xs font-semibold text-ghasla-text-secondary">
                <th className="h-11 px-3 text-right">نوع الغسيل</th>
                <th className="h-11 px-3 text-right">الزيادة</th>
              </tr>
            </thead>
            <tbody>
              {washTypes.map((w) => (
                <tr key={w.id} className="border-t border-[#F4F8F8]">
                  <td className="h-12 px-3 text-ghasla-text-primary">{w.name}</td>
                  <td className="h-12 px-3">
                    {w.mode === "base" ? (
                      <span className="text-ghasla-text-secondary">{w.value}</span>
                    ) : w.mode === "percent" ? (
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1 rounded-lg bg-[#F0FDFD] p-0.5">
                          <button
                            type="button"
                            onClick={() => setUrgentMode("percent")}
                            className={`cursor-pointer rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                              urgentMode === "percent" ? "bg-white text-ghasla-text-link shadow-sm" : "text-ghasla-text-secondary"
                            }`}
                          >
                            نسبة %
                          </button>
                          <button
                            type="button"
                            onClick={() => setUrgentMode("fixed")}
                            className={`cursor-pointer rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                              urgentMode === "fixed" ? "bg-white text-ghasla-text-link shadow-sm" : "text-ghasla-text-secondary"
                            }`}
                          >
                            مبلغ ثابت
                          </button>
                        </div>
                        <div className="flex items-center gap-1">
                          <input
                            className="h-9 w-20 rounded-lg border border-[#E4EFEF] bg-[#F0FDFD] px-2 text-center text-sm outline-none focus:border-[#0D6E6E]"
                            value={urgentValue}
                            onChange={(e) => {
                              setUrgentValue(e.target.value);
                              flash();
                            }}
                          />
                          <span className="text-xs text-ghasla-text-secondary">{urgentMode === "percent" ? "%" : "ر.س"}</span>
                        </div>
                      </div>
                    ) : (
                      <button type="button" className="cursor-pointer text-sm font-medium text-ghasla-text-link hover:underline">
                        {w.value}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delivery */}
      <div className="rounded-[20px] bg-white p-6 shadow-card">
        <h2 className="text-lg font-semibold text-ghasla-text-heading">إعدادات التوصيل</h2>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[420px] text-sm">
            <thead>
              <tr className="text-xs font-semibold text-ghasla-text-secondary">
                <th className="h-11 px-3 text-right">نوع التوصيل</th>
                <th className="h-11 px-3 text-right">السعر</th>
                <th className="h-11 px-3 text-right">المدة</th>
              </tr>
            </thead>
            <tbody>
              {delivery.map((d) => (
                <tr key={d.name} className="border-t border-[#F4F8F8]">
                  <td className="h-12 px-3 text-ghasla-text-primary">{d.name}</td>
                  <td className="h-12 px-3">
                    <input
                      className="h-9 w-20 rounded-lg border border-[#E4EFEF] bg-[#F0FDFD] text-center text-sm outline-none focus:border-[#0D6E6E]"
                      defaultValue={d.price}
                      onChange={flash}
                    />
                  </td>
                  <td className="h-12 px-3 text-ghasla-text-secondary">{d.duration}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-5 flex items-center justify-between rounded-xl bg-[#F0FDFD] px-4 py-3">
          <div>
            <p className="text-sm font-medium text-ghasla-text-primary">توصيل مجاني للطلبات فوق</p>
            <p className="text-xs text-ghasla-text-secondary">حدد الحد الأدنى للحصول على توصيل مجاني</p>
          </div>
          <div className="flex items-center gap-3">
            <input
              className="h-9 w-20 rounded-lg border border-[#E4EFEF] bg-white text-center text-sm outline-none focus:border-[#0D6E6E]"
              defaultValue="100"
              onChange={flash}
            />
            <span className="text-sm text-ghasla-text-secondary">ر.س</span>
            <Toggle size="sm" checked onChange={() => {}} />
          </div>
        </div>
      </div>

      {saved && (
        <div className="fixed bottom-6 left-6 flex items-center gap-2 rounded-xl bg-[#E8F9F0] px-4 py-2.5 text-sm font-medium text-[#27AE60] shadow-card">
          <i className="ri-checkbox-circle-fill" />
          تم الحفظ ✓
        </div>
      )}
    </div>
  );
}